import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ProductCard from '@/components/ui/ProductCard';
import ProductCardSkeleton from '@/components/ui/ProductCardSkeleton';
import { usePagination } from '@/hooks/usePagination';
import { useFeaturedProducts } from '@/hooks/useFeaturedProducts';
import type { Product } from '@/types/product';
export const FeaturedProductsSkeleton = () => <section className="py-6 sm:py-8 bg-cream-50" style={{ minHeight: '1100px' }}>
    <div className="container-custom">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Featured Products</h2>
        <div className="h-6 w-32 bg-gray-200 animate-pulse rounded"></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {Array.from({
        length: 8
      }).map((_, i) => <ProductCardSkeleton key={i} />)}
      </div>
    </div>
  </section>;

interface FeaturedProductsContainerProps {
  enabled?: boolean;
}

const FeaturedProductsContainer = ({ enabled = true }: FeaturedProductsContainerProps) => {
  const [activeTab, setActiveTab] = useState<'popular' | 'featured' | 'discounted' | 'new'>('featured');

  // Fetch products with improved error handling
  const {
    data: products = [],
    isLoading,
    error
  } = useFeaturedProducts({ enabled });

  // Helper for safe numeric sorting
  const safeNumber = (val: number | undefined | null) => val ?? 0;

  // Helper for consistent boolean checking across environments
  const isTruthy = (value: any): boolean => {
    return value === true || value === 'true' || value === 1 || value === '1';
  };

  // Filter and memoize products by tab with improved boolean handling
  const {
    popular,
    featured,
    discounted,
    newArrivals
  } = useMemo(() => {
    console.log(`Processing ${products.length} products for tabs`);
    if (!Array.isArray(products) || products.length === 0) {
      return {
        popular: [],
        featured: [],
        discounted: [],
        newArrivals: []
      };
    }
    const popular = products.filter(p => {
      const isTopSeller = isTruthy(p.is_top_seller);
      const hasStock = isTruthy(p.stock_available);
      return isTopSeller && hasStock;
    }).sort((a, b) => {
      // Sort by created date for consistent ordering
      return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
    });
    const featured = products.filter(p => {
      const isFavorite = isTruthy(p.is_favorite);
      const hasStock = isTruthy(p.stock_available);
      return isFavorite && hasStock;
    }).sort((a, b) => {
      return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
    });
    const discounted = products.filter(p => {
      const isSpecialDiscount = isTruthy(p.is_special_discount);
      const hasStock = isTruthy(p.stock_available);
      const hasSpecialPrice = p.special_price && safeNumber(p.special_price) > 0;
      return isSpecialDiscount && hasStock && hasSpecialPrice;
    }).sort((a, b) => {
      // Sort by discount amount (higher discount first)
      const aDiscount = safeNumber(a.incl_price) - safeNumber(a.special_price);
      const bDiscount = safeNumber(b.incl_price) - safeNumber(b.special_price);
      return bDiscount - aDiscount;
    });
    const newArrivals = products.filter(p => {
      const isNewArrival = isTruthy(p.is_new_arrival);
      const hasStock = isTruthy(p.stock_available);
      return isNewArrival && hasStock;
    }).sort((a, b) => {
      // Sort by newest first
      return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
    });

    // Log the counts for debugging
    const counts = {
      popular: popular.length,
      featured: featured.length,
      discounted: discounted.length,
      newArrivals: newArrivals.length
    };
    console.log('Tab product counts:', counts);
    return {
      popular,
      featured,
      discounted,
      newArrivals
    };
  }, [products]);
  const productMap = {
    popular,
    featured,
    discounted,
    new: newArrivals
  };
  const currentProducts = productMap[activeTab] || featured;
  const {
    currentItems,
    hasMore,
    loadMore,
    reset,
    totalShown,
    totalItems
  } = usePagination<Product>({
    items: currentProducts,
    initialPageSize: 8,
    // Increased for better UX
    maxItems: 24 // Increased limit
  });
  const handleTabChange = (tab: typeof activeTab) => {
    console.log(`Switching to tab: ${tab}`);
    setActiveTab(tab);
    reset();
  };

  // Loading skeleton
  if (isLoading) {
    return <FeaturedProductsSkeleton />;
  }

  // Error state
  if (error) {
    console.error('Featured products error:', error);
    return <section className="py-6 sm:py-8 bg-cream-50">
        <div className="container-custom text-center">
          <h2 className="text-2xl font-bold mb-4">Featured Products</h2>
          <p className="text-red-500 mb-2">Unable to load featured products.</p>
          <p className="text-sm text-gray-500 mb-4">Please try refreshing the page.</p>
          <Link to="/products" className="text-springbok-500 hover:text-springbok-600 inline-flex items-center">
            Browse All Products <ChevronRight size={18} className="ml-1" />
          </Link>
        </div>
      </section>;
  }

  // No products state
  if (!products.length) {
    return <section className="py-6 sm:py-8 bg-cream-50">
        <div className="container-custom text-center">
          <h2 className="text-2xl font-bold mb-4">Featured Products</h2>
          <p className="text-gray-500 mb-2">No featured products available at the moment.</p>
          <p className="text-sm text-gray-400 mb-4">Check back soon for new arrivals!</p>
          <Link to="/products" className="text-springbok-500 hover:text-springbok-600 inline-flex items-center">
            Browse All Products <ChevronRight size={18} className="ml-1" />
          </Link>
        </div>
      </section>;
  }
  // minHeight reserves the loaded section's actual measured height (8 products, tabs,
  // Load More block, desktop 1350px viewport) so the placeholder-snapshot-to-live-data
  // swap that step 2's delayed query causes doesn't reflow this section — same technique
  // as HomeVideoSection's wrapper in Index.tsx, applied here instead of a delay change.
  return <section className="py-6 sm:py-8 bg-cream-50" style={{ minHeight: '1100px' }}>
      <div className="container-custom">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Featured Products</h2>
          <Link to="/products" className="text-springbok-500 hover:text-springbok-600 inline-flex items-center">
            View All <ChevronRight size={18} className="ml-1" />
          </Link>
        </div>

        <Tabs value={activeTab} className="w-full" onValueChange={handleTabChange}>
          <div className="mb-4 overflow-x-auto">
            <TabsList className="bg-cream-100 p-1 flex gap-2">
              <TabsTrigger value="featured">Featured ({featured.length})</TabsTrigger>
              <TabsTrigger value="popular">Top Sellers ({popular.length})</TabsTrigger>
              <TabsTrigger value="discounted">Specials ({discounted.length})</TabsTrigger>
              <TabsTrigger value="new">New ({newArrivals.length})</TabsTrigger>
            </TabsList>
          </div>

          {currentProducts.length === 0 ? <div className="text-center py-8 text-gray-500">
              <p>No products in this category yet.</p>
              <p className="text-sm mt-1">Try another tab or check back later!</p>
            </div> : <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {/* All cards lazy (no eager/high): the prerendered copy of this grid ships in
                    dist/index.html for EVERY route, and on deep routes it sits in a
                    display:none subtree — lazy + display:none is never fetched, eager is.
                    The homepage LCP card is instead preloaded by the inline head script in
                    index.html (URL injected by scripts/prerender-homepage.tsx). */}
                {currentItems.map((p, i) => <ProductCard key={p.id} product={p} priority={false} index={i + 2} />)}
              </div>

              {hasMore && <div className="text-center mt-4">
                  <Button 
                    onClick={loadMore}
                    data-gradient="load-more"
                  >
                    Load More <ChevronRight size={16} className="ml-1" />
                  </Button>
                  <p className="text-sm text-gray-500 mt-2">
                    Showing {totalShown} of {totalItems} products
                  </p>
                </div>}
            </>}
        </Tabs>
      </div>
    </section>;
};
export default FeaturedProductsContainer;